import { faker } from '@faker-js/faker';

import { ProfilesResponse } from '@/features/user';

export const generateUser = (overrides: Record<string, unknown> = {}) => ({
  id: faker.string.uuid(),
  email: faker.internet.email(),
  password: faker.internet.password(),
  base_lead_id: faker.string.uuid(),
  role: faker.helpers.arrayElement(['student', 'parent', 'admin']),
  practice_id: faker.string.uuid(),
  selected_profile: faker.datatype.boolean(),
  enrolled_courses: generateCourses(faker.number.int({ min: 1, max: 3 })),
  primary_contact: faker.datatype.boolean(),
  registration: generateFirstRegistered(faker.number.int({ min: 1, max: 3 })),
  ...overrides,
});

export const generateFirstRegistered = (overrides = {}) => ({
  id: faker.string.uuid(),
  registered_timestamp: faker.date.recent().getTime().toString(),
  registered_timestamp_readable: faker.date
    .recent()
    .toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  source: faker.helpers.arrayElement(['parent_app', 'student_dashboard']),
  ...overrides,
});

// Course name generator function
function* courseNameGenerator() {
  const courseNames = [
    'Math Wizard',
    'English',
    'School Curriculum',
    'Grade 3 Academic Maths ',
    'Apex Academy',
  ];
  let index = 0;

  while (true) {
    yield courseNames[index];
    index = (index + 1) % courseNames.length;
  }
}

const courseNameGen = courseNameGenerator();

export const generateCourse = (overrides = {}) => ({
  course_id: faker.string.uuid(),
  course_name: courseNameGen.next().value as string,
  created_at: faker.date.recent().toISOString(),
  status: faker.helpers.arrayElement(['active', 'inactive', 'draft']),
  batch_id: faker.helpers.arrayElement([faker.string.uuid(), undefined]),
  created_by: faker.string.uuid(),
  modified_by: faker.string.uuid(),
  product_id: faker.string.uuid(),
  product_name: faker.helpers.arrayElement([
    'Math Wizard',
    'School Curriculum',
    'batches_math_xcellence',
    'Apex Academy',
  ]),
  modified_on: faker.date.recent().getTime(),
  created_on: faker.date.recent().getTime(),
  is_active: faker.datatype.boolean(),
  current_credit: {
    bought_credit: faker.number.int({ min: 1, max: 100 }),
    bhanzu_money_credit: faker.number.int({ min: 1, max: 100 }),
    free_credit: faker.number.int({ min: 1, max: 100 }),
  },
  profile_id: faker.string.uuid(),
  variant_id: faker.string.uuid(),
  // category: faker.helpers.arrayElement(['FREE', 'PAID']), // old: might be needed for merge changes
  invoice_link: faker.internet.url(),
  category: faker.helpers.arrayElement([
    'live',
    'on_demand',
    'recorded_stream',
  ]),
  sd_onboarding: faker.datatype.boolean(),
  is_trial: faker.datatype.boolean(),
  for_scheduled_payments: false,
  ...overrides,
});

export const generateProfile = (
  overrides: Record<string, unknown> = {}
): ProfilesResponse.Profile => ({
  id: faker.string.uuid(),
  name: faker.person.fullName(),
  mobile_number: `+91 ${faker.string.numeric(5)} ${faker.string.numeric(5)}`,
  email: faker.internet.email(),
  role: faker.helpers.arrayElement(['STUDENT', 'PARENT']),
  avatar: faker.helpers.arrayElement(['bird0001', 'cat0001', 'dog0001']),
  base_lead_id: faker.string.uuid(),
  practice_id: faker.string.uuid(),
  grade: faker.helpers.arrayElement([
    'UKG-1',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
  ]),
  absolute_grade: faker.helpers.arrayElement([
    'UKG-1',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
  ]),
  address: faker.location.streetAddress(),
  zipcode: faker.location.zipCode(),
  city: faker.location.city(),
  state: faker.location.state(),
  country: faker.location.countryCode('alpha-2'),
  payment_status: faker.helpers.arrayElement(['PAID', 'PENDING', 'OVERDUE']),
  is_communication: faker.datatype.boolean(),
  doubtgpt_enabled: faker.datatype.boolean(),
  selected_variant_id: faker.string.uuid(),
  gender: faker.helpers.arrayElement(['Male', 'Female', 'Other']),
  enrolled_courses: generateCourses(faker.number.int({ min: 1, max: 3 })),
  first_registered: generateFirstRegisteredList(
    faker.number.int({ min: 1, max: 3 })
  ),
  onboarding_candidate: faker.datatype.boolean(),
  timezone: faker.helpers.arrayElement([
    'Asia/Kolkata',
    'America/New_York',
    'Europe/London',
    'Asia/Singapore',
    'America/Los_Angeles',
  ]),
  ...overrides,
});

export const generateUsers = (count: number, overrides = {}) =>
  Array.from({ length: count }, () => generateUser(overrides));

export const generateCourses = (count: number, overrides = {}) =>
  Array.from({ length: count }, () => generateCourse(overrides));

export const generateProfiles = (count: number, overrides = {}) =>
  Array.from({ length: count }, () => generateProfile(overrides));

export const generateFirstRegisteredList = (count: number, overrides = {}) =>
  Array.from({ length: count }, () => generateFirstRegistered(overrides));

export const generateAccount = (
  withSecondaryContact: boolean,
  noOfStudents: number
) => {
  const baseLeadId = faker.string.uuid();
  const overrides = { base_lead_id: baseLeadId };
  const parent1 = generateUser({
    ...overrides,
    role: 'parent',
    primary_contact: true,
  });
  const profiles = [parent1];
  if (withSecondaryContact) {
    const parent2 = generateUser(overrides);
    profiles.push(parent2);
  }

  const courses = generateCourses(2);

  Array.from({ length: noOfStudents }, () => {
    const student = generateUser(overrides);
    profiles.push({
      ...student,
      id: faker.string.uuid(),
      enrolled_courses: courses,
      role: 'student',
    });
    return false;
  });

  return profiles;
};
